import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useEffect,
  useRef,
} from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
} from "@material-tailwind/react";
import "react-datepicker/dist/react-datepicker.css";
import { Icon } from "../AccordionIcon";
import { ISession, ISessionTab } from "../../interfaces/Session";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  fetchSessions,
  storeSessions,
  resetState,
  deleteSession,
} from "../../store/ProgramSessions";
import { toast } from "react-toastify";
import moment from "moment";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import DeleteConfirmation from "../ConfirmBox";
import { useTranslation } from "react-i18next";

const Session: React.FC<ISessionTab> = ({ programId, startDate }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = React.useState(0);
  const [sessions, setSessions] = useState<ISession[]>([]);
  const [sessionToEdit, setSessionToEdit] = useState<ISession | null>(null);
  const [sessionToDelete, setSessionToDelete] = useState<ISession | null>(null);
  const [accordionIndexToEdit, setAccordionIndexToEdit] = useState<
    number | undefined
  >();
  const [startTime, setStartTime] = useState<Date | null>(moment().set({hours: 9, minutes: 0}).toDate()); // January 1, 1900, 09:00:00
  const [endTime, setEndTime] = useState<Date | null>(moment().set({hours: 9, minutes: 15}).toDate()); // January 1, 1900, 09:00:00
  const [accordionIndexToDelete, setAccordionIndexToDelete] = useState<
    number | undefined
  >();
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
const storedData = localStorage.getItem("loginData");
  const {
    programSessions: fetchedSessions,
    loading,
    error,
  } = useSelector((state: RootState) => state.programSessions);
  let accessToken = "";

  useEffect(() => {
    if (accessToken && !isLoggedIn) {
      // navigate('/');
    }
  }, [accessToken, isLoggedIn, navigator]);

  useEffect(() => {
    dispatch(resetState());
    if (programId) {
      dispatch(fetchSessions({ programId }));
    }
  }, [dispatch]);

  useEffect(() => {
    const sessionsToSet = fetchedSessions.map((session) => {
      return {
        ...session,
        startDate: moment(session.startDate).toDate(),
        time: moment(session.startTime).toDate(),
      };
    });
    setSessions([...sessionsToSet]);
  }, [fetchedSessions]);

  useEffect(() => {
    if (sessionToEdit !== null) {
      renderSessions();
    }
    if (sessions.length && sessionToEdit === null) {
      setOpen(sessions.length - 1);
    }
  }, [sessions, sessionToEdit]);

  useEffect(() => {
    if (open === accordionIndexToEdit && titleInputRef.current) {
      // Focus on the title input
      titleInputRef.current.focus();
    }
  }, [accordionIndexToEdit, open]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        {loading && (
          <div
            className="border-t-4 border-solid h-16 w-16 border-b-4 border-r-4 border-l-4 rounded-full animate-spin"
            style={{ borderColor: "#B4804A" }}
          />
        )}
      </div>
    );
  }

  if (error) {
    return <div>{t("common.error", "An error occurred")}: {error}</div>;
  }

  if (!fetchedSessions) {
    return <div>{t("adminPrograms.noData", "No data available")}</div>;
  }
  const handleOpen = (value: number) => setOpen(open === value ? 0 : value);
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => { 
    e.preventDefault();
    if (!sessions || sessions.length === 0) {
      toast.warning(t("adminPrograms.sessionsForm.validDetailsWarning", "Please provide valid session details"));
      return;
    }

    const invalidIndex = sessions.findIndex((s) => !s.title || s.title.trim() === "");
    if (invalidIndex !== -1) {
      setOpen(invalidIndex);
      setAccordionIndexToEdit(invalidIndex);
      toast.error(
        t("adminPrograms.sessionsForm.validDetailsWarning", "Please provide valid session details")
      );
      return;
    }

    const sessionsToUpdate = sessions.map((session) => {
      return {
        ...session,
        startDate: session.startDate,
        startTime: session.startTime ?? moment().set({hours: 9, minutes: 0}).toDate(),
        endTime: session.endTime ?? moment().set({hours: 9, minutes: 15}).toDate(),
      };
    });

    dispatch(
      storeSessions({
        programId: programId as string,
        sessions: sessionsToUpdate,
      })
    )
      .then(() => {
        toast.success(t("adminPrograms.sessionsForm.storedSuccess", "Sessions stored successfully"));
      })
      .catch((err: { message: any }) => {
        if (err instanceof Error) {
          console.error(err.message);
        }
      });
  };

  const handleTitleChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setSessions((prevSessions) => {
      const sessionsUpdated = prevSessions.map(
        (prevSession, prevSessionIndex) => {
          if (index === prevSessionIndex) {
            return { ...prevSession, title: e.target.value };
          }
          return prevSession;
        }
      );
      return sessionsUpdated;
    });
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    index: number
  ) => {
    setSessions((prevSessions) => {
      const sessionsUpdated = prevSessions.map(
        (prevSession, prevSessionIndex) => {
          if (index === prevSessionIndex) {
            return { ...prevSession, description: e.target.value };
          }
          return prevSession;
        }
      );
      return sessionsUpdated;
    });
  };

  const handleDateChange = (
    dateToChange: DateObject | DateObject[] | null,
    index: number
  ) => {
    setSessions((prevSessions) => {
      const sessionsUpdated = prevSessions.map(
        (prevSession, prevSessionIndex) => {
          if (index === prevSessionIndex) {
            return {
              ...prevSession,
              startDate: moment(dateToChange?.toString()).toDate(),
            };
          }
          return prevSession;
        }
      );
      return sessionsUpdated;
    });
  };

  const handleTimeChange = (
    timeSelected: DateObject | DateObject[] | null,
    index: number
  ) => {
    if (timeSelected) {
      setSessions((prevSessions) => {
        const sessionsUpdated = prevSessions.map(
          (prevSession, prevSessionIndex) => {
            if (index === prevSessionIndex) {
              return {
                ...prevSession,
                startTime: moment(timeSelected?.toString(), ["HH:mm", "hh:mm A"]).toDate(),
              };
            }
            return prevSession;
          }
        );
        return sessionsUpdated;
      });
    }
  };
  const handleEndTimeChange = (
    timeSelected: DateObject | DateObject[] | null,
    index: number
  ) => {
    if (timeSelected) {
      setSessions((prevSessions) => {
        const sessionsUpdated = prevSessions.map(
          (prevSession, prevSessionIndex) => {
            if (index === prevSessionIndex) {
              return {
                ...prevSession,
                endTime: moment(timeSelected?.toString(), ["HH:mm", "hh:mm A"]).toDate(),
              };
            }
            return prevSession;
          }
        );
        return sessionsUpdated;
      });
    }
  };

  const handleDelete = (
    index: number | undefined,
    session: ISession | null
  ) => {
    setSessionToDelete(null);
    setAccordionIndexToDelete(undefined);
    if (index !== undefined && session) {
      const newSessions = [...sessions];
      newSessions.splice(index, 1);
      setSessions(newSessions);
      if (session?.id && programId) {
        dispatch(
          deleteSession({
            sessionId: session?.id,
            programId: programId?.toString(),
          })
        )
          .then(() => {
            toast.success(t("adminPrograms.sessionsForm.deletedSuccess", "Session deleted successfully"));
          })
          .catch((err: { message: any }) => {
            if (err instanceof Error) {
              console.error(err.message);
            }
          });
      }
    }
  };

  const handleCancel = () => {
    setSessionToDelete(null);
    setAccordionIndexToDelete(undefined);
  };

  const handleSessionEdit = (session: ISession, index: number) => {
    const sessionToUpdate = {
      ...session,
      startDate: moment(session.startDate).toDate(),
      time: moment(session.startTime).toDate(),
    };
    setSessionToEdit(sessionToUpdate);
    setAccordionIndexToEdit(index);
    setOpen(index);
  };
  const lDate=moment(localStorage.getItem("startDate")).format("MM/DD/YYYY");
  const shownew = () => {
    // Add a new session when the button is clicked
    setSessionToDelete(null);
    setAccordionIndexToDelete(undefined);
    setSessions([
      ...sessions,
      {
        title: "",
        startDate: moment(lDate).toDate(),
        description: "",
        time: moment(new Date()).toDate(),
      },
    ]);
    setSessionToEdit(null);
    setAccordionIndexToEdit(sessions.length);
    setOpen(sessions.length);
  };
  
    const renderSessions = () => {
    return sessions.map((session, index) => (
      <Accordion
        placeholder=""
        key={index}
        open={open === index}
        icon={<Icon id={index} open={open} />}
      >
        <AccordionHeader
          placeholder=""
          onClick={() => handleOpen(index)}
          className="bg-[#041B44] text-white px-6 py-2 rounded-t-xl flex-row mt-6 text-lg"
        >
          <span>{t("adminPrograms.sessionsForm.sessionPrefix", "Session")} {index + 1}</span>
        </AccordionHeader>
        <AccordionBody className="p-6 border rounded-b-xl overflow-scroll">
          <div className="absolute top-3 end-20">
            <button
              title={t("adminPrograms.sessionsForm.editSession", "Edit session")}
              type="button"
              className="w-fit pl-4 hover:border-gray-800"
              role="menuitem"
              onClick={(e) => handleSessionEdit(session, index)}
            >
              <img
                src="./images/EditWhite.png"
                alt=""
                className="w-[75%]"
              />
            </button>
            <button
              title={t("adminPrograms.sessionsForm.deleteSession", "Delete session")}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setAccordionIndexToDelete(index);
                setSessionToDelete(session);
                setOpen(index);
              }}
              className="pl-2"
              role="menuitem"
            >
              <img
                src="./images/deleteWhite.png"
                alt=""
                className="w-[75%]"
              />
            </button>
          </div>
          <div className="flex flex-col md:flex-row">
            <div className="mb-4  w-full md:w-2/6  pr-4">
              <label
                className="block text-[#607D8B] text-sm  mb-2"
                htmlFor="title"
              >
                {t("adminPrograms.sessionsForm.title", "Title")}
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={session.title}
                onChange={(e) => handleTitleChange(e, index)}
                className="border-2 border-[#E0E4EC] text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium rounded-[8px] px-3 py-2  p-2 rounded-md w-1/2 focus:outline-none focus:border-gray-500"
                placeholder={t("adminPrograms.sessionsForm.titlePlaceholder", "Please select your title")}
                required
                disabled={!(index === accordionIndexToEdit)}
                ref={(inputRef) => {
                  // Set the ref for the title input
                  if (index === accordionIndexToEdit) {
                    titleInputRef.current = inputRef;
                  }
                }}
              />
            </div>

            <div className="mb-4 w-full md:w-2/6  pr-4">
              <label
                className="block text-[#607D8B] text-sm mb-2"
                htmlFor={`date_${index}`}
              >
                {t("adminPrograms.sessionsForm.date", "Date")}
              </label>
              <DatePicker
                placeholder={t("adminPrograms.sessionsForm.datePlaceholder", "MM/DD/YYYY")}
                inputClass="border-2 text-[#263238] bg-[#EDF2F6] placeholder:text-[#8D98AA] font-[18px] focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 focus:outline-none focus:border-gray-500 w-full"
                containerClassName="w-full"
                calendarPosition="bottom"
                id={`date_${index}`}
                required
                highlightToday={true} 
                disabled={!(index === accordionIndexToEdit)}
                onChange={(startDate) => handleDateChange(startDate, index)}
                format="MM/DD/YYYY"
                value={moment(session.startDate).format("MM/DD/YYYY")}
                minDate={new DateObject()}
              />
            </div>

            <div className="mb-4 w-full md:w-2/6 pr-4">
              <label
                className="block text-[#607D8B] text-sm  mb-2"
                htmlFor="time"
              >
                {t("adminPrograms.sessionsForm.startTime", "Start Time")}
              </label>
              <DatePicker
                id={`time_${index}`}
                value={session.startTime? session.startTime : startTime}
                placeholder="HH:mm"
                inputClass="border-2 text-[#263238] bg-[#EDF2F6] placeholder:text-[#8D98AA] font-[18px] focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 focus:outline-none focus:border-gray-500 w-full"
                disableDayPicker
                containerClassName="w-full"
                format="HH:mm"
                minDate={new DateObject()}
                disabled={!(index === accordionIndexToEdit)}
                plugins={[<TimePicker mStep={15} hideSeconds/>]}
                onChange={(dateTime) => handleTimeChange(dateTime, index)}
              />
            </div>
            <div className="mb-4 w-full md:w-2/6 pr-4">
              <label
                className="block text-[#607D8B] text-sm  mb-2"
                htmlFor="time"
              >
                {t("adminPrograms.sessionsForm.endTime", "End Time")}
              </label>
              <DatePicker
                id={`endtime_${index}`}
                value={session.endTime || endTime}
                placeholder="HH:mm"
                inputClass="border-2 text-[#263238] bg-[#EDF2F6] placeholder:text-[#8D98AA] font-[18px] focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 focus:outline-none focus:border-gray-500 w-full"
                disableDayPicker
                containerClassName="w-full"
                format="HH:mm"
                minDate={new DateObject()}
                disabled={!(index === accordionIndexToEdit)}
                plugins={[<TimePicker mStep={15} hideSeconds/>]}
                onChange={(dateTime) => handleEndTimeChange(dateTime, index)}
              />
            </div>
          </div>
          <div className="mb-4 w-[99%] ">
            <label
              className="block text-[#607D8B] text-sm  mb-2"
              htmlFor="Time"
            >
              {t("adminPrograms.sessionsForm.description", "Description")}
            </label>
            <textarea
              disabled={!(index === accordionIndexToEdit)}
              id="description"
              name="description"
              onChange={(e) => handleDescriptionChange(e, index)}
              value={session.description}
              className="resize-none border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 focus:outline-none focus:border-gray-500"
              rows={4}
              placeholder={t("adminPrograms.sessionsForm.descriptionPlaceholder", "Enter a description")}
            ></textarea>
          </div>
        </AccordionBody>
      </Accordion>
    ));
  };

  const renderDeleteConfirmation = () => {
    if (accordionIndexToDelete !== undefined && sessionToDelete) {
      return (
        <DeleteConfirmation
          onConfirm={() =>
            handleDelete(accordionIndexToDelete, sessionToDelete)
          }
          onCancel={handleCancel}
          message={`${t("adminPrograms.sessionsForm.sessionPrefix", "Session")} ${(accordionIndexToDelete as number) + 1}`}
        />
      );
    }
    return null;
  };

  return (
    <div className="px-8 pb-2 max-h-[calc(100vh-280px)] overflow-y-auto mt-2">
      {renderDeleteConfirmation()}
      <form onSubmit={handleSubmit}>
        {renderSessions()}
        <button
          type="button"
          className="w-full object-cover mt-6 p-2 border-dashed text-[#8C97AC] rounded-lg border-2 border-[#8C97AC] focus:outline-none focus:border-[#8C97AC]-500"
          onClick={shownew}
        >
          <span className="flex flex-row justify-center text-lg font-semibold text-center">
            <img src="./images/plusgray.png" alt="" />
            {t("adminPrograms.sessionsForm.addNewSession", "Add New Sessions")}
          </span>
        </button>
        <button
          type="submit"
          className={`bg-[#B4804A] text-white float-right w-32 py-2 px-4 font-semibold mb-8 mt-8 rounded-md ${
            sessions.length > 0 ? "cursor-pointer" : "cursor-not-allowed"
          }`}
          disabled={sessions.length > 0 ? false : true}
        >
          {t("adminPrograms.sessionsForm.save", "Save")}
        </button>
      </form>
    </div>
  );
};

export default Session;
